import React, { useState } from 'react';

interface ForgotPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

const API_BASE_URL = 'https://api.bringit.co.in/api';

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState<'phone' | 'otp' | 'password'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check if phone exists
      const checkResponse = await fetch(`${API_BASE_URL}/users/check-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.startsWith('+') ? phone : '+91' + phone })
      });

      const checkData = await checkResponse.json();

      if (!checkResponse.ok) {
        setError(checkData.message || 'Phone number not found');
        setLoading(false);
        return;
      }

      setUserEmail(checkData.email);

      // Send OTP
      const formattedPhone = phone.startsWith('+') ? phone : '+91' + phone;
      const otpResponse = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: formattedPhone })
      });

      const otpData = await otpResponse.json();

      if (otpResponse.ok) {
        setStep('otp');
        setError('');
      } else {
        setError(otpData.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formattedPhone = phone.startsWith('+') ? phone : '+91' + phone;
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          otp: otp
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStep('password');
        setError('');
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const formattedPhone = phone.startsWith('+') ? phone : '+91' + phone;
      const response = await fetch(`${API_BASE_URL}/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formattedPhone,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('✅ Password reset successfully! You can now login with your new password.');
        onSuccess();
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <button onClick={onBack} style={styles.backButton}>
            ← Back
          </button>
          <h2 style={styles.title}>🔐 Forgot Password</h2>
          <p style={styles.subtitle}>Reset your password using phone OTP</p>
        </div>

        {/* Step 1: Enter Phone */}
        {step === 'phone' && (
          <form onSubmit={handleSendOTP} style={styles.form}>
            <div style={styles.stepIndicator}>Step 1 of 3</div>
            
            <p style={styles.instruction}>
              Enter your registered phone number to receive an OTP
            </p>

            <input
              type="tel"
              placeholder="Phone Number (10 digits)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              pattern="[0-9]{10}"
              style={styles.input}
            />

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? 'Checking...' : 'Send OTP'}
            </button>
          </form>
        )}

        {/* Step 2: Verify OTP */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} style={styles.form}>
            <div style={styles.stepIndicator}>Step 2 of 3</div>
            
            <div style={styles.successBox}>
              ✅ OTP sent to {phone}
              {userEmail && <div style={{ fontSize: '13px', marginTop: '5px' }}>Account: {userEmail}</div>}
            </div>

            <p style={styles.instruction}>
              Enter the 6-digit OTP sent to your phone
            </p>

            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              maxLength={6}
              pattern="[0-9]{6}"
              style={styles.input}
            />

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              type="button"
              onClick={() => handleSendOTP({ preventDefault: () => {} } as any)}
              style={styles.resendButton}
            >
              Resend OTP
            </button>
          </form>
        )}

        {/* Step 3: Set New Password */}
        {step === 'password' && (
          <form onSubmit={handleResetPassword} style={styles.form}>
            <div style={styles.stepIndicator}>Step 3 of 3</div>
            
            <div style={styles.successBox}>
              ✅ OTP Verified Successfully
            </div>

            <p style={styles.instruction}>
              Create your new password
            </p>

            <input
              type="password"
              placeholder="New Password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              style={styles.input}
            />

            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              style={styles.input}
            />

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '30px'
  },
  backButton: {
    background: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '15px',
    padding: '5px 10px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700' as const,
    color: '#282c3f',
    margin: '10px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '10px 0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '15px'
  },
  stepIndicator: {
    background: 'linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600' as const,
    textAlign: 'center' as const,
    width: 'fit-content',
    margin: '0 auto'
  },
  instruction: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center' as const,
    margin: '10px 0'
  },
  successBox: {
    background: '#d4edda',
    border: '1px solid #28a745',
    color: '#155724',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center' as const
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e9e9eb',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  resendButton: {
    background: 'transparent',
    color: '#6366f1',
    border: '2px solid #6366f1',
    padding: '12px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer'
  },
  error: {
    background: '#f8d7da',
    color: '#721c24',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    border: '1px solid #f5c6cb'
  }
};

export default ForgotPassword;
